import { useEffect, useRef } from 'react';
import type { AvatarTemplate } from '../../types/avatar';
import { asset } from '../../lib/assets';

interface TemplatePreviewCardProps {
  template: AvatarTemplate;
  onSelect: (template: AvatarTemplate) => void;
}

export function TemplatePreviewCard({ template, onSelect }: TemplatePreviewCardProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width, height } = template;
    canvas.width = width;
    canvas.height = height;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      // Fill soft background
      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(0, 0, width, height);

      // Draw silhouette in the center
      ctx.save();
      const cx = width / 2;
      const cy = template.category === 'avatar' ? height / 2 : height * 0.45;
      const headR = template.category === 'avatar' ? 140 : 160;

      ctx.fillStyle = '#cbd5e1';
      // Head
      ctx.beginPath();
      ctx.arc(cx, cy - headR * 0.4, headR, 0, Math.PI * 2);
      ctx.fill();

      // Shoulders
      ctx.beginPath();
      ctx.ellipse(cx, cy + headR * 1.5, headR * 1.8, headR * 1.2, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Render template overlay
      template.renderOverlay(ctx, width, height, {
        logoMode: template.styleVariant.includes('logo') ? 'logo' : 'text',
      });
    };

    draw();

    // If logo wasn't fully loaded yet, redraw when ready
    const logo = new Image();
    logo.src = asset('images/traianologo.png');
    if (!logo.complete) {
      logo.onload = draw;
    }
  }, [template]);

  return (
    <button
      type="button"
      onClick={() => onSelect(template)}
      className="group relative flex flex-col items-center focus:outline-hidden text-left"
      aria-label={`Criar com o ${template.name}`}
    >
      <div
        className={`relative w-full overflow-hidden rounded-2xl bg-white border border-[#d9e0ec] shadow-[0_4px_16px_rgba(6,25,77,0.06)] transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_12px_28px_rgba(6,25,77,0.12)] group-hover:border-[#123ee0]/40 group-focus-visible:ring-4 group-focus-visible:ring-[#16a53a] ${
          template.category === 'avatar' ? 'aspect-square' : 'aspect-[4/5]'
        }`}
      >
        <canvas
          ref={canvasRef}
          className="h-full w-full object-contain pointer-events-none"
        />

        <div className="absolute inset-0 bg-[#06194d]/0 group-hover:bg-[#06194d]/8 transition-colors flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity rounded-full bg-[#06194d] px-4 py-1.5 font-display text-sm font-bold uppercase tracking-wider text-white shadow-md scale-95 group-hover:scale-100 duration-200">
            Personalizar
          </span>
        </div>
      </div>

      <span className="mt-3 block text-center font-display text-lg font-bold uppercase tracking-wide text-[#06194d] group-hover:text-[#123ee0] transition-colors">
        {template.name}
      </span>
      <span className="text-xs text-[#5a6b88] font-medium text-center">
        {template.tagline}
      </span>
    </button>
  );
}
